'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

// Same staged runway philosophy as TopBar, but the footer keeps an in-flow
// reservation while parked so removing the heavy subtree cannot move the
// scrollbar thumb, the document tail, or the observers themselves.
const FOOTER_PREWARM_MARGIN_PX = 3200
const FOOTER_KEEP_MARGIN_PX = 4600
const FOOTER_PARK_DELAY_MS = 900
const FOOTER_REMOUNT_GEOMETRY_LOCK_MS = 450
const FOOTER_SHRINK_CONFIRM_MS = 280
const FOOTER_HEIGHT_EPSILON_PX = 0.5

const ANCHOR_STYLE = Object.freeze({
  position: 'absolute',
  width: 1,
  height: 1,
  margin: 0,
  padding: 0,
  border: 0,
  opacity: 0,
  overflow: 'hidden',
  pointerEvents: 'none',
})

const makeSpacerStyle = (boxHeight, marginTop, marginBottom) => ({
  display: 'block',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  height: `${boxHeight}px`,
  minHeight: `${boxHeight}px`,
  maxHeight: `${boxHeight}px`,
  marginTop: `${marginTop}px`,
  marginRight: 0,
  marginBottom: `${marginBottom}px`,
  marginLeft: 0,
  padding: 0,
  border: 0,
  overflow: 'hidden',
  visibility: 'hidden',
  pointerEvents: 'none',
  flex: '0 0 auto',
})

export default function FooterViewportPark({
  children,
  scope = 'global-footer-tail',
  reserveMarginTopPx = 0,
  reserveMarginBottomPx = 0,
}) {
  const anchorRef = useRef(null)
  const parkTimerRef = useRef(0)
  const shrinkConfirmTimerRef = useRef(0)
  const shrinkCandidateRef = useRef(null)
  const geometryLockTimerRef = useRef(0)
  const geometryLockUntilRef = useRef(0)
  const reservedBoxHeightRef = useRef(0)
  const lastObservedBoxHeightRef = useRef(0)
  const nearRef = useRef(true)
  const withinKeepRangeRef = useRef(true)
  const renderVisualRef = useRef(true)

  // Fail-open first paint. R2 never guesses a footer height: the real visual
  // subtree mounts first, ResizeObserver supplies its responsive border-box,
  // and only then is parking allowed.
  const [renderVisual, setRenderVisual] = useState(true)
  const [reservedBoxHeight, setReservedBoxHeight] = useState(0)

  const reserveMarginTop = Math.max(0, Number(reserveMarginTopPx || 0))
  const reserveMarginBottom = Math.max(0, Number(reserveMarginBottomPx || 0))

  const clearParkTimer = useCallback(() => {
    if (!parkTimerRef.current || typeof window === 'undefined') return
    window.clearTimeout(parkTimerRef.current)
    parkTimerRef.current = 0
  }, [])

  const clearShrinkConfirmation = useCallback(() => {
    if (shrinkConfirmTimerRef.current && typeof window !== 'undefined') {
      window.clearTimeout(shrinkConfirmTimerRef.current)
    }
    shrinkConfirmTimerRef.current = 0
    shrinkCandidateRef.current = null
  }, [])

  const clearGeometryLock = useCallback(() => {
    if (geometryLockTimerRef.current && typeof window !== 'undefined') {
      window.clearTimeout(geometryLockTimerRef.current)
    }
    geometryLockTimerRef.current = 0
    geometryLockUntilRef.current = 0
  }, [])

  const applyReservedBoxHeight = useCallback((nextHeight) => {
    const stableHeight = Math.round(Number(nextHeight || 0) * 1000) / 1000
    if (!Number.isFinite(stableHeight) || stableHeight <= 0.5) return 0

    const previousHeight = Number(reservedBoxHeightRef.current || 0)
    if (Math.abs(stableHeight - previousHeight) <= FOOTER_HEIGHT_EPSILON_PX) {
      return previousHeight
    }

    reservedBoxHeightRef.current = stableHeight
    setReservedBoxHeight(stableHeight)
    return stableHeight
  }, [])

  const submitBoxHeightCandidate = useCallback((nextHeight) => {
    const stableHeight = Math.round(Number(nextHeight || 0) * 1000) / 1000
    if (!Number.isFinite(stableHeight) || stableHeight <= 0.5) return 0

    const currentHeight = Number(reservedBoxHeightRef.current || 0)

    // Growth is accepted immediately so the reservation can never clip a real
    // responsive footer. Shrink is delayed/confirmed to reject transient
    // remount/font/layout dips exactly like the hardened TopBar contour.
    if (currentHeight <= 0.5 || stableHeight > currentHeight + FOOTER_HEIGHT_EPSILON_PX) {
      clearShrinkConfirmation()
      return applyReservedBoxHeight(stableHeight)
    }

    if (Math.abs(stableHeight - currentHeight) <= FOOTER_HEIGHT_EPSILON_PX) {
      clearShrinkConfirmation()
      return currentHeight
    }

    if (
      typeof performance !== 'undefined' &&
      performance.now() < Number(geometryLockUntilRef.current || 0)
    ) {
      clearShrinkConfirmation()
      return currentHeight
    }

    const previousCandidate = shrinkCandidateRef.current
    if (
      !previousCandidate ||
      Math.abs(Number(previousCandidate.height || 0) - stableHeight) > FOOTER_HEIGHT_EPSILON_PX
    ) {
      clearShrinkConfirmation()
      const candidate = { height: stableHeight }
      shrinkCandidateRef.current = candidate
      shrinkConfirmTimerRef.current = window.setTimeout(() => {
        shrinkConfirmTimerRef.current = 0
        if (shrinkCandidateRef.current !== candidate) return
        shrinkCandidateRef.current = null
        applyReservedBoxHeight(candidate.height)
      }, FOOTER_SHRINK_CONFIRM_MS)
    }

    return currentHeight
  }, [applyReservedBoxHeight, clearShrinkConfirmation])

  const mountVisual = useCallback(() => {
    clearParkTimer()
    if (renderVisualRef.current) return

    renderVisualRef.current = true
    clearGeometryLock()
    clearShrinkConfirmation()

    if (typeof window !== 'undefined' && typeof performance !== 'undefined') {
      geometryLockUntilRef.current = performance.now() + FOOTER_REMOUNT_GEOMETRY_LOCK_MS
      geometryLockTimerRef.current = window.setTimeout(() => {
        geometryLockTimerRef.current = 0
        geometryLockUntilRef.current = 0

        // ResizeObserver may have delivered a transient remount size while the
        // lock was active. Re-submit the newest real size when the lock ends so
        // a stale oversized spacer can never accumulate across park cycles.
        const latestObservedHeight = Number(lastObservedBoxHeightRef.current || 0)
        if (latestObservedHeight > 0.5) submitBoxHeightCandidate(latestObservedHeight)
      }, FOOTER_REMOUNT_GEOMETRY_LOCK_MS)
    }

    setRenderVisual(true)
  }, [
    clearGeometryLock,
    clearParkTimer,
    clearShrinkConfirmation,
    submitBoxHeightCandidate,
  ])

  const schedulePark = useCallback(() => {
    if (
      typeof window === 'undefined' ||
      parkTimerRef.current ||
      !renderVisualRef.current ||
      Number(reservedBoxHeightRef.current || 0) <= 0.5
    ) {
      return
    }

    parkTimerRef.current = window.setTimeout(() => {
      parkTimerRef.current = 0

      // The two observer runways are authoritative. Crucially, R2 replaces the
      // visual with an equal in-flow reservation, so this transition cannot
      // change document height and make the footer self-evict like R1 did.
      if (nearRef.current || withinKeepRangeRef.current) return
      if (Number(reservedBoxHeightRef.current || 0) <= 0.5) return

      renderVisualRef.current = false
      clearGeometryLock()
      clearShrinkConfirmation()
      setRenderVisual(false)
    }, FOOTER_PARK_DELAY_MS)
  }, [clearGeometryLock, clearShrinkConfirmation])

  useEffect(() => {
    if (!renderVisual || typeof window === 'undefined') return undefined

    const anchor = anchorRef.current
    const node = anchor?.nextElementSibling || null
    if (!node) return undefined

    // Premium fail-open policy: without ResizeObserver we keep the real footer
    // mounted. No synchronous geometry fallback is allowed to introduce a new
    // forced-layout path just for parking.
    if (typeof ResizeObserver !== 'function') return undefined

    let disposed = false
    const resizeObserver = new ResizeObserver((entries) => {
      if (disposed) return
      const entry = entries?.[0]
      if (!entry) return

      const borderBox = entry.borderBoxSize?.[0] ?? entry.borderBoxSize
      const observedHeight = Number(borderBox?.blockSize ?? entry.contentRect?.height ?? 0)
      if (!Number.isFinite(observedHeight) || observedHeight <= 0.5) return

      lastObservedBoxHeightRef.current = observedHeight
      const applied = submitBoxHeightCandidate(observedHeight)

      if (
        (applied > 0.5 || Number(reservedBoxHeightRef.current || 0) > 0.5) &&
        !nearRef.current &&
        !withinKeepRangeRef.current
      ) {
        schedulePark()
      }
    })

    resizeObserver.observe(node)

    return () => {
      disposed = true
      resizeObserver.disconnect()
    }
  }, [renderVisual, schedulePark, submitBoxHeightCandidate])

  useEffect(() => {
    const anchor = anchorRef.current
    if (!anchor || typeof window === 'undefined') return undefined

    if (typeof IntersectionObserver !== 'function') {
      mountVisual()
      return undefined
    }

    // IMPORTANT: never use the absolute metadata anchor as the distance owner.
    // An absolutely positioned child of a flex container with auto insets gets
    // a flex-derived static position. On narrow/mobile layouts that position can
    // land around the middle of a very tall page, so the anchor may leave the
    // keep runway while the real footer is literally inside the viewport.
    //
    // The rendered visual block (mounted) and its exact in-flow reservation
    // (parked) are geometry-equivalent. Observe that real footprint instead.
    // This also means a tall responsive footer stays intersecting while the
    // viewport travels through any part of it, not only while its top edge is
    // close to the viewport.
    const target = anchor.nextElementSibling || null
    if (!(target instanceof Element)) {
      mountVisual()
      return undefined
    }

    nearRef.current = true
    withinKeepRangeRef.current = true

    const nearObserver = new IntersectionObserver((entries) => {
      const near = !!entries?.[0]?.isIntersecting
      nearRef.current = near

      if (near) {
        mountVisual()
      } else if (!withinKeepRangeRef.current) {
        schedulePark()
      }
    }, {
      root: null,
      rootMargin: `${FOOTER_PREWARM_MARGIN_PX}px 0px ${FOOTER_PREWARM_MARGIN_PX}px 0px`,
      threshold: 0,
    })

    const keepObserver = new IntersectionObserver((entries) => {
      const withinKeepRange = !!entries?.[0]?.isIntersecting
      withinKeepRangeRef.current = withinKeepRange

      if (withinKeepRange) {
        clearParkTimer()
        return
      }

      if (!nearRef.current) schedulePark()
    }, {
      root: null,
      rootMargin: `${FOOTER_KEEP_MARGIN_PX}px 0px ${FOOTER_KEEP_MARGIN_PX}px 0px`,
      threshold: 0,
    })

    nearObserver.observe(target)
    keepObserver.observe(target)

    return () => {
      clearParkTimer()
      nearObserver.disconnect()
      keepObserver.disconnect()
    }
  }, [clearParkTimer, mountVisual, renderVisual, schedulePark])

  useEffect(() => () => {
    clearParkTimer()
    clearGeometryLock()
    clearShrinkConfirmation()
  }, [clearGeometryLock, clearParkTimer, clearShrinkConfirmation])

  const reservedFootprint = reservedBoxHeight > 0
    ? Math.round((reservedBoxHeight + reserveMarginTop + reserveMarginBottom) * 1000) / 1000
    : 0

  return (
    <>
      <span
        ref={anchorRef}
        aria-hidden="true"
        data-ql7-footer-park-anchor={scope}
        data-ql7-footer-park-state={renderVisual ? 'mounted' : 'parked'}
        data-ql7-footer-park-reserved-height={reservedFootprint > 0 ? String(reservedFootprint) : 'measuring'}
        data-ql7-footer-park-reserved-box-height={reservedBoxHeight > 0 ? String(reservedBoxHeight) : 'measuring'}
        style={ANCHOR_STYLE}
      />
      {renderVisual ? children : (
        <span
          aria-hidden="true"
          data-ql7-footer-park-spacer={scope}
          style={makeSpacerStyle(reservedBoxHeight, reserveMarginTop, reserveMarginBottom)}
        />
      )}
    </>
  )
}

export {
  FOOTER_PREWARM_MARGIN_PX,
  FOOTER_KEEP_MARGIN_PX,
  FOOTER_PARK_DELAY_MS,
  FOOTER_REMOUNT_GEOMETRY_LOCK_MS,
  FOOTER_SHRINK_CONFIRM_MS,
}
